// Drawing ```mermaid figures in the browser. Mermaid is large (hundreds of kB),
// so the renderer never bundles it: a figure shows its source until it comes
// into view, then the runtime is loaded once and draws it. Where it comes from
// is the host's choice:
//   - the script-tag bundle loads it from beside itself (instruction.md serves
//     it at /vendor/mermaid/), so an embed needs nothing extra;
//   - an app that bundles its own passes a loader: setMermaidLoader(() => import("mermaid")).
// A diagram that fails to parse keeps its source and says why; nothing throws.
import { LIGHT } from "./styles";

interface MermaidApi {
  initialize(config: Record<string, unknown>): void;
  render(id: string, text: string): Promise<{ svg: string }>;
}
type Loader = () => Promise<{ default: MermaidApi } | MermaidApi>;

let loader: Loader | null = null;
let runtime: Promise<MermaidApi> | null = null;
let theme: string | null = null;
let seq = 0;
// a document re-renders on every edit; a diagram whose text did not change is not drawn again
const drawn = new Map<string, string>();

/** Where Mermaid comes from; call before the first diagram is drawn. */
export function setMermaidLoader(fn: Loader): void { loader = fn; runtime = null; }

function defaultLoader(): Loader | null {
  let base: string;
  try { base = import.meta.url; } catch { return null; }
  if (!/^https?:/.test(base)) return null;
  // built from parts so no bundler resolves it at build time
  const url = new URL(["vendor", "mermaid", "mermaid.esm.min.js"].join("/"), base).href;
  return () => import(/* @vite-ignore */ /* webpackIgnore: true */ url);
}

function load(dark: boolean): Promise<MermaidApi> {
  const want = dark ? "dark" : "neutral";
  if (runtime && theme === want) return runtime;
  const l = loader ?? defaultLoader();
  if (!l) return Promise.reject(new Error("no Mermaid runtime configured"));
  theme = want;
  runtime = l().then((m) => {
    const api = ("default" in m ? m.default : m) as MermaidApi;
    // Mermaid measures labels in the page and the figure draws them in the
    // document's font; naming the renderer's own stack keeps the two the same
    api.initialize({ startOnLoad: false, securityLevel: "strict", theme: want, fontFamily: LIGHT.font, flowchart: { htmlLabels: false } });
    return api;
  });
  runtime.catch(() => { runtime = null; });
  return runtime;
}

const pending = new WeakSet<Element>();
let observer: IntersectionObserver | null = null;

async function draw(fig: HTMLElement): Promise<void> {
  const code = fig.querySelector("pre.fig-src code");
  if (!code || fig.dataset.drawn === code.textContent) return;
  const text = code.textContent ?? "";
  // the theme follows the text colour the figure is drawn in: light text, dark diagram
  const rgb = (getComputedStyle(fig).color.match(/\d+(\.\d+)?/g) ?? ["0", "0", "0"]).map(Number);
  const dark = 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2] > 140;
  try {
    const key = `${dark ? "d" : "l"}:${text}`;
    let svg = drawn.get(key);
    if (!svg) {
      const api = await load(dark);
      svg = (await api.render(`imd-mermaid-${++seq}`, text)).svg;
      drawn.set(key, svg);
      if (drawn.size > 50) drawn.delete(drawn.keys().next().value!);
    }
    fig.querySelector(".fig-out")?.remove();
    fig.querySelector(".fig-err")?.remove();
    const out = document.createElement("div");
    out.className = "fig-out";
    out.innerHTML = svg; // Mermaid's own output, with securityLevel "strict" (it sanitizes labels)
    fig.prepend(out);
    fig.dataset.drawn = text;
  } catch (err) {
    fig.querySelector(".fig-err")?.remove();
    const note = document.createElement("div");
    note.className = "fig-err";
    note.textContent = `Diagram not drawn: ${err instanceof Error ? err.message.split("\n")[0] : String(err)}`;
    fig.append(note);
  }
}

/** Draw every Mermaid figure under `root` as it comes into view. */
export function enhanceDiagrams(root: ParentNode): void {
  if (typeof window === "undefined") return;
  const figs = root.querySelectorAll<HTMLElement>('figure.fig[data-fig="mermaid"]');
  if (!figs.length) return;
  if (!("IntersectionObserver" in window)) { figs.forEach((f) => void draw(f)); return; }
  observer ??= new IntersectionObserver((entries) => {
    for (const en of entries) if (en.isIntersecting) { observer!.unobserve(en.target); pending.delete(en.target); void draw(en.target as HTMLElement); }
  }, { rootMargin: "200px" });
  figs.forEach((f) => {
    // drawn before (the document re-rendered around it): put it back at once
    const t = f.querySelector("pre.fig-src code")?.textContent ?? "";
    if ([...drawn.keys()].some((k) => k.slice(2) === t)) { void draw(f); return; }
    if (!pending.has(f)) { pending.add(f); observer!.observe(f); }
  });
}
